import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { HTTPService } from "../../../services/httpService";
import { toast } from 'react-toastify';
import AddStaffModal from './AddStaffModal';
import ManagerCheckInModal from './ManagerCheckInModal';
import ManagerCheckOutModal from './ManagerCheckOutModal';
import { getStaffTimeStatus } from '../../../services/timeTrackingService';
import {
    mapToDealerRole,
    UNIFIED_ROLES,
    getUnifiedRoleBadgeColor,
    getUnifiedRoleLabel
} from '../../../util/staffRoles';
import { convertFromUTC } from '../../../util/convert_timezone';

const API = new HTTPService();

/**
 * StaffManagement - Manage staff for installer or dealer companies
 *
 * For Installers (Specialists):
 * - View all staff at their company
 * - Add new staff members (technicians or managers)
 * - Edit staff roles
 * - Deactivate staff accounts
 *
 * For Dealers:
 * - View all staff AND contacts (unified list)
 * - Add new staff members (sales, finance, manager)
 * - Upgrade contacts to staff (grant login access)
 * - Edit staff roles
 * - Deactivate staff accounts
 */
class StaffManagement extends Component {
  state = {
    staffList: [],
    loading: true,
    error: null,

    // Modal state
    showAddModal: false,
    editingStaff: null, // null = adding new, object = editing existing

    // Company info
    companyId: null,
    companyName: '',

    // Upgrade state (dealers only)
    upgradingUserId: null,

    // Role filter
    roleFilter: 'all',

    // Time tracking state (installers only)
    timeTrackingEnabled: false, // Whether time tracking is enabled for this location
    timeTrackingStatus: {}, // Map of user_id -> time status
    timeTrackingLoading: false,

    // Manager check-in/check-out modals
    showCheckInModal: false,
    showCheckOutModal: false,
    selectedTechnician: null, // Technician being checked in/out
    checkActionLoading: null // User ID currently being processed
  };

  async componentDidMount() {
    const { typeId, companyName, userType } = this.props;
    this.setState({ companyId: typeId, companyName });
    await this.loadStaff();

    // Load time tracking settings and status for installers only
    if (userType !== 'dealer') {
      const isEnabled = this.loadTimeTrackingSettings();
      // Only load status if time tracking is enabled
      if (isEnabled) {
        await this.loadTimeTrackingStatus();
      }
    }
  }

  // Load time tracking enabled setting from company data
  // Returns whether time tracking is enabled
  loadTimeTrackingSettings = () => {
    try {
      const companyData = JSON.parse(localStorage.getItem('companyAPI') || '{}');
      // Time tracking mode: 'none' | 'voluntary' | 'required'
      // - none: feature disabled, hide UI
      // - voluntary/required: feature enabled, show UI
      const timeTrackingMode = companyData.time_tracking_mode || 'none';
      const timeTrackingEnabled = timeTrackingMode !== 'none';
      this.setState({ timeTrackingEnabled });
      return timeTrackingEnabled;
    } catch (error) {
      console.error('Error loading time tracking settings:', error);
      return false;
    }
  }

  loadStaff = async () => {
    try {
      this.setState({ loading: true, error: null });

      const { typeId, userType } = this.props;
      let staff = [];

      if (userType === 'dealer') {
        // Dealers: Use dealerContacts endpoint which returns all dealer users
        const { data } = await API.get(`/dealerContacts/${typeId}`);
          console.log('Staff data from API:', data);
        staff = data || [];
      } else {
        // Installers: Use users endpoint with filters
        const { data } = await API.get(`/users?company_id=${typeId}&user_type=installer`);
          console.log('Staff data from API:', data);

        staff = data || [];
      }

      this.setState({
        staffList: staff,
        loading: false
      });
    } catch (error) {
      console.error('Error loading staff:', error);
      this.setState({
        error: 'Failed to load staff list',
        loading: false
      });
    }
  };

  // Load time tracking status for all staff (installers only)
  loadTimeTrackingStatus = async () => {
    const { typeId, userType } = this.props;

    // Only load for installers
    if (userType === 'dealer') return;

    try {
      this.setState({ timeTrackingLoading: true });

      const statusData = await getStaffTimeStatus(typeId);

      // Convert array to map for easy lookup
      const statusMap = {};
      if (Array.isArray(statusData)) {
        statusData.forEach(item => {
          // Derive is_checked_in from session data - checked in if session exists and is not closed
          // Active statuses: 'open', 'flagged' (still working, just flagged for review)
          const activeStatuses = ['open', 'flagged'];
          const isCheckedIn = item.session_id && activeStatuses.includes(item.session_status);
          statusMap[item.user_id] = {
            is_checked_in: isCheckedIn,
            check_in_at: item.check_in_at,
            session_id: item.session_id,
            session_status: item.session_status
          };
        });
      }

      this.setState({
        timeTrackingStatus: statusMap,
        timeTrackingLoading: false
      });
    } catch (error) {
      console.error('Error loading time tracking status:', error);
      this.setState({ timeTrackingLoading: false });
    }
  };

  // Open check-in modal
  handleOpenCheckInModal = (staff) => {
    this.setState({
      showCheckInModal: true,
      selectedTechnician: staff
    });
  };

  // Open check-out modal
  handleOpenCheckOutModal = (staff) => {
    const { timeTrackingStatus } = this.state;
    const status = timeTrackingStatus[staff.User_id];

    this.setState({
      showCheckOutModal: true,
      selectedTechnician: {
        ...staff,
        check_in_at: status?.check_in_at
      }
    });
  };

  // Close check-in modal
  handleCloseCheckInModal = () => {
    this.setState({
      showCheckInModal: false,
      selectedTechnician: null
    });
  };

  // Close check-out modal
  handleCloseCheckOutModal = () => {
    this.setState({
      showCheckOutModal: false,
      selectedTechnician: null
    });
  };

  // Handle successful check-in
  handleCheckInSuccess = (technician) => {
    toast.success(`${technician.user_name} has been checked in.`);
    this.loadTimeTrackingStatus();
  };

  // Handle successful check-out
  handleCheckOutSuccess = (technician, durationMinutes) => {
    const duration = durationMinutes ? ` (${Math.round(durationMinutes)} minutes)` : '';
    toast.success(`${technician.user_name} has been checked out.${duration}`);
    this.loadTimeTrackingStatus();
  };

  // Get time tracking status for a staff member
  getTimeStatus = (userId) => {
    const { timeTrackingStatus } = this.state;
    return timeTrackingStatus[userId] || null;
  };

  // Format check-in time
  formatCheckInTime = (checkInAt) => {
    if (!checkInAt) return '-';
    return convertFromUTC(checkInAt).format('h:mm A');
  };

  handleAddStaff = () => {
    this.setState({
      showAddModal: true,
      editingStaff: null
    });
  };

  handleEditStaff = (staff) => {
    this.setState({
      showAddModal: true,
      editingStaff: staff
    });
  };

  handleCloseModal = () => {
    this.setState({
      showAddModal: false,
      editingStaff: null
    });
  };

  handleSaveStaff = async (staffData) => {
    try {
      const { typeId, userType } = this.props;
      const isEditing = !!this.state.editingStaff;

      if (isEditing) {
        // Update existing staff - include ALL required fields to prevent nulls
        // The backend replaces all fields, so we must send everything
        const existingStaff = this.state.editingStaff;

        await API.put(`/users/${existingStaff.User_id}`, {
          // Fields being updated
          userName: staffData.name,
          userPhoneNumber: staffData.phone || existingStaff.user_phone_number || '',
          role: staffData.role,  // NEW: Use 'role' instead of 'userRole'
          active: staffData.active ? 1 : 0,
          // Required fields that must be preserved
          email: existingStaff.Email,
          companyId: existingStaff.Company_id || typeId,
          userType: userType
        });
        alert('Staff member updated successfully!');
      } else {
        // Create new staff member
        const createData = {
          email: staffData.email,
          userName: staffData.name,
          userPhoneNumber: staffData.phone,
          companyId: typeId,
          userType: userType,
          role: staffData.role,  // NEW: Use 'role' instead of 'userRole'
          active: 1
        };

        const { data: result } = await API.post('/users', createData);

        // Create Cognito account and send invite email (if requested)
        if (result.userId && staffData.createLogin) {
          try {
            await API.post('/auth/create-user', {
              email: staffData.email,
              userId: result.userId
            });
            alert('Staff member created! They will receive an email to set their password.');
          } catch (cognitoError) {
            console.error('Cognito account creation failed:', cognitoError);
            alert('Staff member created, but email invite failed. You may need to manually send a password reset.');
          }
        } else if (result.userId && !staffData.createLogin) {
          // Contact only (no login) - for dealers
          alert('Contact added successfully! They will receive notifications but cannot log in.');
        }
      }

      this.handleCloseModal();
      await this.loadStaff();

    } catch (error) {
      console.error('Error saving staff:', error);
      alert('Error saving staff member: ' + (error.response?.data?.message || error.message));
    }
  };

  // Upgrade a contact to staff (dealers only) - creates Cognito account
  handleUpgradeToStaff = async (staff) => {
    if (!window.confirm(`This will send ${staff.user_name} an email to set up their login and access the system. Continue?`)) {
      return;
    }

    this.setState({ upgradingUserId: staff.User_id });

    try {
      // Create Cognito account for existing user
      await API.post('/auth/create-user', {
        email: staff.Email,
        userId: staff.User_id
      });

      alert(`${staff.user_name} has been upgraded to staff! They will receive an email to set their password.`);
      await this.loadStaff();
    } catch (error) {
      console.error('Error upgrading contact:', error);
      if (error.response?.status === 409) {
        alert('This user already has login access.');
      } else {
        alert('Error upgrading contact: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      this.setState({ upgradingUserId: null });
    }
  };

  handleDeactivateStaff = async (staff) => {
    if (!window.confirm(`Are you sure you want to deactivate ${staff.user_name}? They will no longer be able to log in.`)) {
      return;
    }

    try {
      await API.put(`/users/${staff.User_id}`, {
        active: 0
      });
      alert('Staff member deactivated.');
      await this.loadStaff();
    } catch (error) {
      console.error('Error deactivating staff:', error);
      alert('Error deactivating staff member.');
    }
  };

  handleReactivateStaff = async (staff) => {
    try {
      await API.put(`/users/${staff.User_id}`, {
        active: 1
      });
      alert('Staff member reactivated.');
      await this.loadStaff();
    } catch (error) {
      console.error('Error reactivating staff:', error);
      alert('Error reactivating staff member.');
    }
  };

  handleResendInvite = async (staff) => {
    try {
      await API.post('/auth/request-password-reset', {
        email: staff.Email
      });
      alert('Password reset email sent to ' + staff.Email);
    } catch (error) {
      console.error('Error sending invite:', error);
      alert('Error sending password reset email.');
    }
  };

  getRoleBadge = (role, originalRole = null) => {
    // NEW: Use unified role system
    const normalizedRole = role ? role.toLowerCase().trim() : '';

    // Check if it's a unified role first
    const isUnifiedRole = UNIFIED_ROLES.some(r => r.value === normalizedRole);
    if (isUnifiedRole) {
      return (
        <span className={`badge badge-${getUnifiedRoleBadgeColor(normalizedRole)}`}>
          {getUnifiedRoleLabel(normalizedRole)}
        </span>
      );
    }

    // LEGACY: Fall back to old role display for backward compatibility
    const { userType } = this.props;

    if (userType === 'dealer') {
      // For dealers, try to map the role if it's not a standard role
      const mappedRole = mapToDealerRole(role) || normalizedRole;
      switch (mappedRole) {
        case 'manager':
          return <span className="badge badge-primary">Manager</span>;
        case 'finance':
          return <span className="badge badge-info">Finance</span>;
        case 'sales':
          return <span className="badge badge-success">Sales</span>;
        default:
          // Show original role if we couldn't map it
          return <span className="badge badge-secondary">{originalRole || role || 'Contact'}</span>;
      }
    } else {
      // Installer roles - case insensitive matching
      switch (normalizedRole) {
        case 'owner':
          return <span className="badge badge-danger">Owner/Operator</span>;
        case 'manager':
          return <span className="badge badge-primary">Manager</span>;
        case 'technician':
          return <span className="badge badge-success">Technician</span>;
        default:
          // Show "Staff" for null/empty roles (legacy users)
          return <span className="badge badge-secondary">Staff</span>;
      }
    }
  };

  getStatusBadge = (active) => {
    return active === 1 || active === true
      ? <span className="badge badge-success">Active</span>
      : <span className="badge badge-secondary">Inactive</span>;
  };

  render() {
    const {
      staffList, loading, error, showAddModal, editingStaff, upgradingUserId, roleFilter,
      showCheckInModal, showCheckOutModal, selectedTechnician, timeTrackingEnabled, timeTrackingLoading
    } = this.state;
    const { user, userType, typeId } = this.props;
    const isDealer = userType === 'dealer';
    const isInstaller = userType !== 'dealer';

    // Count staff by role - NEW: Include unified roles
    let roleCounts = {};
    // Count unified roles
    roleCounts.admin = staffList.filter(s => (s.role || s.user_role) === 'admin').length;
    roleCounts.manager = staffList.filter(s => {
      const role = s.role || s.user_role;
      return role === 'manager' || (isDealer && mapToDealerRole(s.user_role) === 'manager');
    }).length;
    roleCounts.technician = staffList.filter(s => {
      const role = s.role || s.user_role;
      return role === 'technician' || (!isDealer && s.user_role === 'technician');
    }).length;
    roleCounts.viewer = staffList.filter(s => (s.role || s.user_role) === 'viewer').length;

    // Legacy role counts for backward compatibility
    if (isDealer) {
      roleCounts.sales = staffList.filter(s => mapToDealerRole(s.user_role) === 'sales' || s.user_role === 'sales').length;
      roleCounts.finance = staffList.filter(s => mapToDealerRole(s.user_role) === 'finance' || s.user_role === 'finance').length;
      roleCounts.contact = staffList.filter(s => !s.user_role && !s.role).length;
    }
    roleCounts.all = staffList.length;

    // Filter staff by role
    let filteredStaff = staffList;
    if (roleFilter !== 'all') {
      // Check for unified roles first
      const isUnifiedRoleFilter = UNIFIED_ROLES.some(r => r.value === roleFilter);
      if (isUnifiedRoleFilter) {
        filteredStaff = staffList.filter(s => (s.role || s.user_role) === roleFilter);
      } else if (isDealer) {
        if (roleFilter === 'contact') {
          filteredStaff = staffList.filter(s => !s.user_role && !s.role);
        } else {
          filteredStaff = staffList.filter(s => mapToDealerRole(s.user_role) === roleFilter || s.user_role === roleFilter);
        }
      } else {
        filteredStaff = staffList.filter(s => s.user_role === roleFilter);
      }
    }

    return (
      <div className="container-fluid mt-4">
        <div className="row mb-4">
          <div className="col">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <select
                  className="form-control mr-3"
                  style={{ width: 'auto' }}
                  value={roleFilter}
                  onChange={(e) => this.setState({ roleFilter: e.target.value })}
                >
                  <option value="all">All Roles ({roleCounts.all})</option>
                  {/* NEW: Unified roles */}
                  {roleCounts.admin > 0 && <option value="admin">Admins ({roleCounts.admin})</option>}
                  {roleCounts.manager > 0 && <option value="manager">Managers ({roleCounts.manager})</option>}
                  {roleCounts.technician > 0 && <option value="technician">Technicians ({roleCounts.technician})</option>}
                  {roleCounts.viewer > 0 && <option value="viewer">Viewers ({roleCounts.viewer})</option>}
                  {/* Legacy roles for backward compatibility */}
                  {isDealer && roleCounts.sales > 0 && <option value="sales">Sales ({roleCounts.sales})</option>}
                  {isDealer && roleCounts.finance > 0 && <option value="finance">Finance ({roleCounts.finance})</option>}
                  {isDealer && roleCounts.contact > 0 && <option value="contact">Contacts ({roleCounts.contact})</option>}
                </select>
              </div>
              <button
                className="btn btn-primary"
                onClick={this.handleAddStaff}
              >
                Add {isDealer ? 'Team Member' : 'Staff Member'}
              </button>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="alert alert-danger">
            {error}
            <button
              className="btn btn-link"
              onClick={this.loadStaff}
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading staff...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && staffList.length === 0 && (
          <div className="text-center py-5">
            <i className="fa-sharp fa-regular fa-users fa-3x text-muted mb-3"></i>
            <h5>No Staff Members Yet</h5>
            <p className="text-muted">
              Add technicians and managers to assign work and track service completion.
            </p>
            <button
              className="btn btn-primary"
              onClick={this.handleAddStaff}
            >
              <i className="fa-sharp fa-regular fa-plus mr-2"></i>
              Add Your First Staff Member
            </button>
          </div>
        )}

        {/* No Results for Filter */}
        {!loading && !error && staffList.length > 0 && filteredStaff.length === 0 && (
          <div className="text-center py-5">
            <p className="text-muted">No staff members match the selected filter.</p>
          </div>
        )}

        {/* Staff List */}
        {!loading && !error && filteredStaff.length > 0 && (
          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    {isDealer && <th>Access</th>}
                    {isInstaller && timeTrackingEnabled && <th>Checked In</th>}
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStaff.map(staff => {
                    const isCurrentUser = staff.User_id === user?.User_id;
                    const isActive = staff.active === 1 || staff.active === true;
                    // For dealers: check cognito_status to determine if user has login access
                    // If cognito_status doesn't exist, we'll show "Unknown" and allow upgrade attempt
                    const hasLoginAccess = staff.cognito_status && staff.cognito_status !== 'none';
                    const isUpgrading = upgradingUserId === staff.User_id;

                    return (
                      <tr key={staff.User_id} className={!isActive ? 'table-secondary' : ''}>
                        <td>
                          {staff.user_name}
                          {isCurrentUser && (
                            <span className="badge badge-info ml-2">You</span>
                          )}
                        </td>
                        <td>{staff.Email}</td>
                        <td>{staff.user_phone_number || '-'}</td>
                        <td>{this.getRoleBadge(staff.role || staff.user_role, staff.contact_role)}</td>
                        {isDealer && (
                          <td>
                            {hasLoginAccess ? (
                              <span className="badge badge-success">
                                <i className="fa-sharp fa-regular fa-check mr-1"></i>Staff
                              </span>
                            ) : staff.cognito_status === 'pending' ? (
                              <span className="badge badge-warning">Pending</span>
                            ) : (
                              <span className="badge badge-secondary">Contact</span>
                            )}
                          </td>
                        )}
                        {/* Time Tracking Column - Installers only, when time tracking is enabled */}
                        {isInstaller && timeTrackingEnabled && (() => {
                          const timeStatus = this.getTimeStatus(staff.User_id);
                          const isCheckedIn = timeStatus?.is_checked_in;

                          return (
                            <td>
                              <div className="d-flex align-items-center">
                                {/* Check-in time display - only show when checked in */}
                                {isCheckedIn && (
                                  <span className="text-success mr-2">
                                    {this.formatCheckInTime(timeStatus.check_in_at)}
                                  </span>
                                )}

                                {/* Check In/Out Button */}
                                {isActive && (
                                  <button
                                    className={`btn btn-sm ${isCheckedIn ? 'btn-success' : 'btn-outline-secondary'}`}
                                    onClick={() => isCheckedIn
                                      ? this.handleOpenCheckOutModal(staff)
                                      : this.handleOpenCheckInModal(staff)
                                    }
                                    disabled={timeTrackingLoading}
                                    title={isCheckedIn ? 'Click to check out' : 'Click to check in'}
                                  >
                                    {timeTrackingLoading ? (
                                      <span className="spinner-border spinner-border-sm"></span>
                                    ) : isCheckedIn ? (
                                      <>
                                        <i className="fa-sharp fa-regular fa-sign-out mr-1"></i>
                                        <span className="d-none d-md-inline">Check Out</span>
                                      </>
                                    ) : (
                                      <>
                                        <i className="fa-sharp fa-regular fa-sign-in mr-1"></i>
                                        <span className="d-none d-md-inline">Check In</span>
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                            </td>
                          );
                        })()}
                        <td>{this.getStatusBadge(staff.active)}</td>
                        <td>
                          {/* Edit Button */}
                          <button
                            className="btn btn-sm btn-outline-primary mr-1"
                            onClick={() => this.handleEditStaff(staff)}
                            title="Edit staff member"
                          >
                            <i className="fa-sharp fa-regular fa-edit"></i>
                          </button>

                          {/* Time History Button - Installers only */}
                          {isInstaller && (
                            <Link
                              to={`/staff/${staff.User_id}/time`}
                              className="btn btn-sm btn-outline-info mr-1"
                              title="View time history"
                            >
                              <i className="fa-sharp fa-regular fa-history"></i>
                            </Link>
                          )}

                          {/* Upgrade Button - Dealers only, for contacts without login */}
                          {isDealer && isActive && !hasLoginAccess && staff.cognito_status !== 'pending' && (
                            <button
                              className="btn btn-sm btn-outline-success mr-1"
                              onClick={() => this.handleUpgradeToStaff(staff)}
                              title="Upgrade to staff (grant login access)"
                              disabled={isUpgrading}
                            >
                              {isUpgrading ? (
                                <span className="spinner-border spinner-border-sm"></span>
                              ) : (
                                <i className="fa-sharp fa-regular fa-arrow-up"></i>
                              )}
                            </button>
                          )}

                          {/* Deactivate/Reactivate - Don't show for current user */}
                          {!isCurrentUser && (
                            isActive ? (
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => this.handleDeactivateStaff(staff)}
                                title="Deactivate staff member"
                              >
                                <i className="fa-sharp fa-regular fa-ban"></i>
                              </button>
                            ) : (
                              <button
                                className="btn btn-sm btn-outline-success"
                                onClick={() => this.handleReactivateStaff(staff)}
                                title="Reactivate staff member"
                              >
                                <i className="fa-sharp fa-regular fa-check"></i>
                              </button>
                            )
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add/Edit Modal */}
        <AddStaffModal
          show={showAddModal}
          onHide={this.handleCloseModal}
          onSave={this.handleSaveStaff}
          onResendInvite={this.handleResendInvite}
          editingStaff={editingStaff}
          userType={userType}
        />

        {/* Manager Check-In Modal - Installers only, when time tracking is enabled */}
        {isInstaller && timeTrackingEnabled && (
          <ManagerCheckInModal
            show={showCheckInModal}
            onHide={this.handleCloseCheckInModal}
            onSuccess={this.handleCheckInSuccess}
            technician={selectedTechnician}
            locationId={typeId}
            actorUserId={user?.User_id}
          />
        )}

        {/* Manager Check-Out Modal - Installers only, when time tracking is enabled */}
        {isInstaller && timeTrackingEnabled && (
          <ManagerCheckOutModal
            show={showCheckOutModal}
            onHide={this.handleCloseCheckOutModal}
            onSuccess={this.handleCheckOutSuccess}
            technician={selectedTechnician}
            locationId={typeId}
            actorUserId={user?.User_id}
          />
        )}
      </div>
    );
  }
}

export default StaffManagement;
